<template>
  <!-- 顶部导航：透明覆盖在轮播图之上；滚动后半透明白背景 + 文本变深色。
       历史要求记录：保留原有滚动交互；新增“商城”下拉，背景与顶栏完全一致，避免叠加更白。
       2025-12: 优化语言切换为 CN/EN 矩形开关 
       2026-03: 移除移动端缩放逻辑，改为响应式布局，确保字体和Logo在移动端保持足够大小 -->
  <nav 
    class="navbar" 
    :class="{ 
      'navbar-scrolled': isScrolled || props.forceScrolled
    }"
  >
    <div class="navbar-scaler">
      <div class="nav-left">
        <div class="logo">
          <a href="https://zimimodel.com" style="display: block;">
            <img src="@/assets/logo.png" alt="Logo">
          </a>
        </div>

        <!-- 搜索框：移至商标左侧 -->
        <div class="nav-search" :class="{ open: isSearchOpen }" ref="searchRef">
          <div class="input-wrapper">
            <input 
              ref="searchInputRef"
              v-model="searchKeyword" 
              type="text" 
              :placeholder="isEn ? 'Search...' : '搜索...'" 
              @keyup.enter="handleSearch"
            />
          </div>
          <div class="icon-wrapper" @click="handleSearchClick">
            <Search class="search-icon" />
          </div>
        </div>
      </div>

      <div class="nav-center">
        <!-- “商城”支持下拉菜单（悬停/手机单击触发） --><!-- 下拉菜单：宽度与“商城”一致；背景/字体随导航栏状态一致 -->
        <div class="nav-links">
          <!-- <div
            class="nav-item shop"
            ref="shopRef"
            @mouseenter="onEnterShop"
            @mouseleave="onLeaveShop"
          >
          <a
            href="https://zimimodel.net/en/shop"
            class="shop-trigger"
            role="button"
            aria-haspopup="true"
            :aria-expanded="isShopOpen ? 'true' : 'false'"
            target="_blank"
            rel="noopener"
            @click="onShopTriggerClick"
          >商城</a>
            <div class="submenu" :class="{ open: isShopOpen }">
              <a href="https://zimimodel.net/en/shop/category/1" target="_blank" rel="noopener">飞机</a>
              <a href="https://zimimodel.net/en/shop/category/4" target="_blank" rel="noopener">装甲车</a>
              <a href="https://zimimodel.net/en/shop/category/26" target="_blank" rel="noopener">无人车</a>
              <a href="https://zimimodel.net/en/shop/category/21" target="_blank" rel="noopener">战舰</a>
              <a href="https://zimimodel.net/en/shop/category/22" target="_blank" rel="noopener">兵人</a>
              <a href="https://zimimodel.net/en/shop/category/23" target="_blank" rel="noopener">配件</a>            
            </div>
          </div> -->
          
          <!-- <a href="#" @click="scrollToDistributor">经销商</a> -->
          <a href="https://b.zimimodel.com/" target="_blank" rel="noopener">商城</a>
          <router-link to="/socials" target="_blank" rel="noopener">资讯</router-link>
          
          <router-link to="/contact">联系</router-link>
        </div>
      </div>

      <div class="nav-right">
        <!-- 语言切换：矩形 CN/EN 切换 -->
        <div class="lang-switch">
          <router-link :to="route.path" class="lang-opt" :class="{ active: !isEn }">CN</router-link>
          <router-link :to="targetEnPath" class="lang-opt" :class="{ active: isEn }">EN</router-link>
        </div>
        <a
          class="user-center-link"
          href="https://b.zimimodel.com/web/#/pages/login/login"
          aria-label="用户中心"
        >
          <img src="https://cdn.cnbj1.fds.api.mi-img.com/staticsfile/visual/user.png" alt="用户中心">
        </a>
        <!-- <a href="https://zimimodel.net/web/login" target="_blank" rel="noopener">登录</a>
        <a href="https://zimimodel.net/web/signup" target="_blank" rel="noopener">注册</a> -->
        <!-- <router-link to="/bulk" target="_blank" rel="noopener">批量采购</router-link> -->
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
/**
 * Navbar（响应式顶栏）
 * 
 * 功能概述：
 * 1. 布局：左侧 Logo + 导航链接，右侧登录/注册。
 * 2. 交互：
 *    - 滚动变色：页面滚动超过 50px 后，背景变白半透明，文字变深。
 *    // 因用户反馈，去除了停止操作 10 秒后自动上滑隐藏的功能
 *    - 下拉菜单：“产品”与“订阅”支持悬停展开，点击外部自动关闭。
 *    - 平滑滚动：点击“经销商”平滑滚动至底部地图。
 * 3. 适配：
 *    - 使用 clamp() 实现响应式字体与间距。
 *    - (已移除) 引入缩放机制（Scaler）：在窄屏下不再整体缩放，而是保证字体和Logo大小。
 */

import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { Search } from 'lucide-vue-next';

// --- 属性定义 ---
const props = defineProps({
  // 因某些页面（如批量采购）背景为白色，需要强制导航栏显示为“已滚动”状态（黑字）
  forceScrolled: {
    type: Boolean,
    default: false
  }
});

// --- 状态与引用 ---
const route = useRoute();
const router = useRouter();
const isScrolled = ref(false); // 是否已滚动（控制背景色）
const isSearchOpen = ref(false);
const searchKeyword = ref(''); 

// DOM 引用（用于点击外部检测）
const searchRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);

// --- 计算属性 ---
// 当前语言状态 (根据路由前缀判断)
const isEn = computed(() => route.path.startsWith('/eng'));

// 目标语言路径计算
const targetEnPath = computed(() => {
  const path = route.path;
  if (!path.startsWith('/eng')) {
    if (path === '/') return '/eng';
    return `/eng${path}`;
  }
  return path;
});

// 是否为触摸设备 (仅在挂载后检测)
const isTouchDevice = ref(false);

// --- 核心逻辑：滚动与窗口适配 ---

/**
 * 滚动事件处理
 * 更新滚动状态
 * // 因用户需求，去除了自动隐藏导航栏的计时逻辑
 */
const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

// // --- 交互逻辑：下拉菜单 ---

// // 通用开关切换
// const toggleShop = () => isShopOpen.value = !isShopOpen.value;
// const onEnterShop = () => isShopOpen.value = true;
// const onLeaveShop = () => isShopOpen.value = false;

// // 触摸设备上的点击处理（防止点击直接跳转）
// const handleTouchClick = (e: MouseEvent, stateRef: typeof isShopOpen) => {
//   if (isTouchDevice.value) {
//     e.preventDefault();
//     stateRef.value = !stateRef.value;
//   }
// };

// const onShopTriggerClick = (e: MouseEvent) => handleTouchClick(e, isShopOpen);

// 点击文档外部关闭菜单
const onDocClick = (e: MouseEvent) => {
  const target = e.target as Node;
  if (searchRef.value && !searchRef.value.contains(target)) isSearchOpen.value = false;
};

// 搜索框交互逻辑
const handleSearchClick = () => {
  if (!isSearchOpen.value) {
    isSearchOpen.value = true;
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  } else {
    handleSearch();
  }
};

const handleSearch = () => {
  if (searchKeyword.value.trim()) {
    const url = `https://b.zimimodel.com/web/#/pages/goods-search/goods-search?keywords=${encodeURIComponent(searchKeyword.value.trim())}`;
    window.open(url, '_blank');
    isSearchOpen.value = false;
    searchKeyword.value = '';
  } else {
    isSearchOpen.value = false;
  }
};

// --- 业务逻辑：订阅与导航 ---

/**
 * 滚动到经销商地图区域
 */
const scrollToDistributor = async (e: MouseEvent) => {
  e.preventDefault();
  
  // 如果不在首页，先跳转到首页
  if (route.path !== '/') {
    await router.push('/');
    // 等待 DOM 更新
    setTimeout(() => {
      const el = document.getElementById('distributor-map');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    return;
  }

  const el = document.getElementById('distributor-map');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
  document.addEventListener('click', onDocClick, { passive: true });
  
  // 初始状态检查
  handleScroll();
  
  // 检测触摸设备
  isTouchDevice.value = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || ('ontouchstart' in window);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  document.removeEventListener('click', onDocClick);
});
</script>

<style scoped lang="scss">
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  /* 
   * 注意：.navbar 本身是容器，现在通过 .navbar-scaler 来控制内部布局
   * 所以 .navbar 不需要 flex 布局属性，只需要定位和背景
   */
  display: block; 
  
  /* 使用 clamp 实现随窗口缩放的左右内边距 */
  /* 因移除缩放逻辑，调整最小值为适宜移动端阅读的大小 */
  --nav-font-size: clamp(15px, 1.4vw, 16px); /* 最小15px，确保可读性 */
  --nav-gap-x: clamp(4px, 1vw, 12px); /* 缩小最小间距以容纳更多内容 */
  --nav-padding-x: clamp(10px, 4vw, 50px); /* 缩小最小padding */
  --nav-padding-y: 2px; /* 顶栏上下内边距（用于计算下拉与顶栏相邻而不叠加） */
  /* 统一顶栏与下拉菜单的背景/文字变量，确保视觉完全一致 */
  --navbar-bg: transparent;
  --navbar-backdrop: none;
  --navbar-shadow: none;
  --nav-text-color: white;
  --dropdown-offset: 0px; /* 默认透明状态不偏移 */
  --logo-height: clamp(38px, 3vw, 44px); /* 增大Logo最小值，确保可见性 */
  --user-icon-filter: brightness(0) invert(1);
  --user-icon-hover-filter: brightness(0) saturate(100%) invert(45%) sepia(94%) saturate(2200%) hue-rotate(357deg) brightness(102%) contrast(102%);
  
  /* 语言切换按钮颜色变量 */
  --lang-border-color: rgba(255,255,255,0.8);
  --lang-active-bg: rgba(255, 255, 255, 0.5);
  --lang-active-text: #333;
  --lang-inactive-text: white;

  /* padding 转移到 scaler 或者保持在这里？
     如果保持在这里，scale 会连 padding 一起缩放，这是符合预期的。
     因为 padding 是 navbar 的一部分。
  */
  padding: var(--nav-padding-y) 0; /* 上下 padding，左右由 scaler 处理或继承 */
  /* 实际上，padding-x 应该应用在 scaler 内部，或者 navbar 上。
     如果在 navbar 上，当 scaler 宽度为 950px 时，navbar 宽度也是 100%（即 360px）。
     这样 padding-x clamp 会生效为小数值。
     scaler 缩放后，内容会更小。
     
     修正：为了保证缩放后的比例正确，我们将 padding-x 应用在 scaler 内部。
     所以 navbar 只有 padding-y。
  */
  
  background-color: transparent;
  z-index: 1000;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.navbar-scaler {
  /* 默认样式，在 JS 中会被覆盖或增强 */
  width: 100%;
  box-sizing: border-box;
  padding: 0 var(--nav-padding-x);
  
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
}

.navbar-scrolled {
  /* 滚动后同步变量值，供顶栏自身与下拉菜单使用 */
  --navbar-bg: rgba(255, 255, 255, 0.7);
  --navbar-backdrop: blur(10px);
  --navbar-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  --nav-text-color: #333;
  
  /* 滚动后语言切换颜色 */
  --lang-border-color: #333;
  --lang-active-bg: #333;
  --lang-active-text: white;
  --lang-inactive-text: #333;
  --user-icon-filter: brightness(0) saturate(100%);

  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  /* 滚动状态下，给下拉一个向下偏移，避免与顶栏半透明背景叠加 */
  --dropdown-offset: calc(var(--nav-padding-y) + 7px);
}

.nav-left {
  display: flex;
  align-items: center;
  justify-self: start;
}

.nav-center {
  display: flex;
  justify-content: center;
  justify-self: center;
  min-width: 0;
}

.logo {
  display: flex;
  align-items: center; /* 使 logo 与文字居中对齐 */
}
.logo img {
  /* 随窗口缩放 logo 高度 */
  height: var(--logo-height);
  display: block; /* 避免图片默认基线导致与文字不在同一水平线 */
}

.nav-links {
  display: flex;
  margin-left: 0;
  /* 保持横向排列且不换行，整体通过字体与间距缩小来适配 */
  flex-wrap: nowrap;
  white-space: nowrap;
  align-items: center; /* 垂直居中 */
}

.nav-links a, .nav-right a {
  color: white;
  text-decoration: none;
  /* 单项左右间距与字体随窗口缩放 */
  margin: 0 var(--nav-gap-x);
  font-size: var(--nav-font-size);
  /* 顶栏字体不加粗 */
  font-weight: 400;
  transition: color 0.3s ease;
  /* 为图标与文字统一基线，避免默认 inline 的垂直偏差 */
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.nav-right {
  /* 防止右侧入口被压缩换行，保持与左侧同一行 */
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-self: end;
}

.nav-right .user-center-link {
  margin-right: 0;
}

.nav-search {
  display: flex;
  align-items: center;
  margin-left: calc(var(--nav-gap-x) * 2);
  color: white;
  position: relative; /* 为下拉输入框提供定位基准 */
}

.nav-search .icon-wrapper {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-search .search-icon {
  width: clamp(20px, 1.8vw, 24px);
  height: clamp(20px, 1.8vw, 24px);
  transition: color 0.3s ease;
}

.nav-search .icon-wrapper:hover .search-icon {
  color: #FF6900;
}

.nav-search .input-wrapper {
  position: absolute;
  top: calc(100% + 15px); /* 在导航栏下方展开，稍微留点间距 */
  left: 0;
  width: 200px; /* 固定展开宽度 */
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: opacity 0.3s ease, transform 0.3s ease, visibility 0.3s ease;
  background-color: var(--navbar-bg); /* 跟随导航栏滚动变色 */
  backdrop-filter: var(--navbar-backdrop);
  box-shadow: var(--navbar-shadow);
  border-radius: 4px;
  padding: 5px 10px;
}

.nav-search.open .input-wrapper {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.nav-search input {
  width: 100%;
  padding: 6px 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--nav-text-color); /* 随导航栏文本颜色 */
  font-size: var(--nav-font-size);
  border-bottom: 1px solid var(--lang-border-color); /* 随滚动变色 */
}

.nav-search input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.navbar-scrolled .nav-search input::placeholder {
  color: rgba(0, 0, 0, 0.4);
}

.navbar-scrolled .nav-search {
  color: #333;
}

.nav-right .user-center-link img {
  width: clamp(20px, 1.8vw, 24px);
  height: clamp(20px, 1.8vw, 24px);
  display: block;
  filter: var(--user-icon-filter);
  transition: filter 0.3s ease;
}

.nav-right .user-center-link:hover img,
.nav-right .user-center-link:active img {
  filter: var(--user-icon-hover-filter);
}

.navbar-scrolled .nav-links a,
.navbar-scrolled .nav-right a {
  color: #333;
}

.nav-links a:hover,
.nav-links a:active,
.nav-links .router-link-active,
.nav-right a:hover,
.nav-right a:active {
  color: #FF6900;
}

// /* 下拉菜单（“商城”宽度一致） */
// .nav-item.shop {
//   position: relative;
//   display: inline-flex;
// }

// .nav-item.shop .submenu {
//   position: absolute;
//   /* 用户反馈：下拉与顶栏在白色半透明状态下发生“双层叠加”，导致视觉更白；
//    * 通过下移使两者“相邻不叠加”。默认偏移等于顶栏的上下内边距。
//    * 如需微调，请修改 --dropdown-offset 变量（建议 6px~12px 微调）：
//    * 例如在 .navbar 上设置：--dropdown-offset: 10px;
//    */
//   top: calc(100% + var(--dropdown-offset, var(--nav-padding-y)));
//   left: 0;
//   width: 100%; /* 宽度与“商城”一致 */
//   padding: 6px 0;
//   visibility: hidden;
//   opacity: 0;
//   transform: translateY(-6px);
//   transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
//   /* 与顶栏背景完全一致（透明/半透明白 + 模糊/阴影） */
//   background-color: var(--navbar-bg);
//   backdrop-filter: var(--navbar-backdrop);
//   -webkit-backdrop-filter: var(--navbar-backdrop);
//   box-shadow: var(--navbar-shadow);
//   color: var(--nav-text-color);
//   z-index: 1001;
// }

// .nav-item.shop:hover .submenu,
// .nav-item.shop .submenu.open {
//   visibility: visible;
//   opacity: 1;
//   transform: translateY(0);
// }

// .nav-item.shop .submenu a {
//   display: block;
//   margin: 0; /* 覆盖 .nav-links a 的左右 margin，避免竖排溢出 */
//   padding: 6px 0;
//   font-size: var(--nav-font-size);
//   font-weight: 400;
//   text-decoration: none;
//   text-align: center;
//   transition: color 0.3s ease;
//   color: inherit; /* 继承父层颜色，与顶栏一致 */
// }

/* 邮箱下拉菜单样式 */
/* 语言切换 - 矩形开关样式 */
.lang-switch {
  display: flex;
  align-items: center;
  border: 1px solid var(--lang-border-color);
  border-radius: 3px;
  overflow: hidden;
  margin: 0 var(--nav-gap-x); /* 保持与其他链接一致的间距 */
  font-size: calc(var(--nav-font-size) * 0.82); /* 稍微小一点的字体 */
  height: 1.42em; /* 固定高度 */
  line-height: 1.42em;
}

.lang-switch .lang-opt {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  height: 100%;
  color: var(--lang-inactive-text); /* 默认颜色 */
  background-color: transparent;
  text-decoration: none;
  transition: all 0.3s ease;
  margin: 0; /* 清除 .nav-links a 的 margin */
  cursor: pointer;
  font-weight: 500;
}

.lang-switch .lang-opt.active {
  background-color: var(--lang-active-bg);
  color: var(--lang-active-text);
}

.lang-switch .lang-opt:not(.active):hover {
  opacity: 0.8;
  color: #FF6900;
}

.lang-switch .lang-opt:active {
  color: #FF6900;
}
</style>
